/**
 * **Geldbeträge in Symphenia** — eine Schreibweise für das ganze System.
 *
 * `1.234,50`: Punkt als Tausender-Trennzeichen, immer zwei Nachkommastellen,
 * ohne Währungszeichen (Auftraggeber 20.9.2026 — und ausdrücklich auch für
 * jedes Feld, das später dazukommt).
 *
 * Warum nicht die Sprache des Browsers: `de-AT` setzt als Tausender-Trennzeichen
 * ein schmales Leerzeichen — aus `1.234,50` würde `1 234,50`. Für jede deutsche
 * Sprachvariante gilt darum die Schreibweise von `de-DE`, und weil die
 * Oberfläche in Österreich läuft, gilt sie auch für einen englischen Browser.
 *
 * **Nicht hier zu Hause:** das Beleg-PDF und die Belegmail. Sie folgen der
 * **Belegsprache** — ein Angebot auf Englisch schreibt `1,234.50` für seinen
 * Kunden (`document-pdf-template.ts`, `document-payments.service.ts`).
 */

/** Die Schreibweise der Oberfläche und der Listen-Exporte. */
export const GELD_LOCALE = 'de-DE'

const GELD_OPTIONEN = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const

/** `1234.5` → `1.234,50`; leer bei `null`, `undefined` und `NaN`. */
export function geldText(wert: number | null | undefined): string {
  if (wert === null || wert === undefined || Number.isNaN(wert)) return ''
  return new Intl.NumberFormat(GELD_LOCALE, GELD_OPTIONEN).format(wert)
}

/**
 * Ein **Einzelpreis**: wie `geldText`, aber mit bis zu vier Nachkommastellen, wo der Preis sie trägt —
 * `1.234,50`, `0,1234`. Ein Preis je Stück, Meter oder Kilo ist oft feiner als ein Cent; auf zwei Stellen
 * gerundet stünde im Feld etwas anderes, als gerechnet wird.
 */
export function preisText(wert: number | null | undefined, maxStellen = 4): string {
  if (wert === null || wert === undefined || Number.isNaN(wert)) return ''
  return new Intl.NumberFormat(GELD_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, maxStellen) }).format(wert)
}

/**
 * Derselbe Betrag mit dem Zeichen seiner Währung — `1.234,50 €`. Eine unbekannte
 * Währung fällt auf die Zahl allein zurück, statt die Anzeige zu sprengen.
 */
export function geldMitWaehrung(wert: number | null | undefined, waehrung: string): string {
  if (wert === null || wert === undefined || Number.isNaN(wert)) return ''
  try {
    return new Intl.NumberFormat(GELD_LOCALE, { style: 'currency', currency: waehrung, ...GELD_OPTIONEN }).format(wert)
  } catch {
    return geldText(wert)
  }
}
